import { debounce } from "lodash";
import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import {
  getDatasetNetworks,
  getLayer,
  getNetworkAnimations,
  getNetworkAnimationStates,
  getProjectNetworks,
  createNetworkAnimation,
  deleteNetworkAnimation,
  deleteNetworkAnimationState,
  createNetworkAnimationState,
  updateNetworkAnimationState,
} from "@/api/rest";
import type {
  Dataset,
  Layer,
  Network,
  NetworkAnimation,
  NetworkAnimationConfig,
  NetworkState,
} from "@/types";
import {
  usePanelStore,
  useProjectStore,
  useStyleStore,
  useMapStore,
  useLayerStore,
  useAppStore,
} from ".";

export const useNetworkStore = defineStore("network", () => {
  const panelStore = usePanelStore();
  const projectStore = useProjectStore();
  const styleStore = useStyleStore();
  const mapStore = useMapStore();
  const layerStore = useLayerStore();
  const appStore = useAppStore();

  const loadingNetworks = ref<boolean>(false);
  const availableNetworks = ref<Network[]>([]);
  const currentNetwork = ref<Network>();
  const networkNodesMapLayerId = ref<string>();
  const networkEdgesMapLayerId = ref<string>();
  const availableAnimations = ref<NetworkAnimation[]>([]);
  const currentAnimation = ref<NetworkAnimation>();
  const currentAnimationTick = ref<number>(0);
  const currentAnimationSyncLayers = ref<Layer[]>([]);
  const animationConfig = ref<NetworkAnimationConfig>();
  const loadingStates = ref<boolean>(false);
  const availableAnimationStates = ref<NetworkState[]>([]);
  const hoverNodeIds = ref<number[]>([]);
  const creatingAnimation = ref<boolean>(false);
  const animationToDelete = ref<NetworkAnimation>();
  const newAnimationName = ref<string>();
  const stateToDelete = ref<NetworkState>();
  const animationNeedsSave = ref<boolean>(false);
  const animationSaveLoading = ref<boolean>(false);

  const editAllowed = computed(() => {
    if (!projectStore.currentProject || !appStore.authenticated) return false;
    return ["owner", "collaborator"].includes(
      projectStore.permissions[projectStore.currentProject.id],
    );
  });
  const currentAnimationEditable = computed(() => {
    // If animation was generated by a task, disallow edits
    return !currentAnimation.value?.task_result;
  });
  const currentAnimationState = computed(() => {
    return availableAnimationStates.value?.find(
      (state) => state.index === currentAnimationTick.value,
    );
  });
  const currentAnimationStateComponents = computed(() => {
    return currentAnimationState.value?.components.toSorted(
      (a, b) => b.nodes.length - a.nodes.length,
    );
  });
  const deactivatedNodes = computed(() => {
    if (!animationConfig.value || !currentAnimationState.value) return [];
    let deactivatedNodes: Set<number> = new Set();
    if (animationConfig.value.time_mode === "slider") {
      deactivatedNodes = deactivatedNodes.union(
        new Set(currentAnimationState.value.deactivated_nodes),
      );
    } else if (animationConfig.value.time_mode === "colormap") {
      availableAnimationStates.value.forEach((state) => {
        deactivatedNodes = deactivatedNodes.union(
          new Set(state.deactivated_nodes),
        );
      });
    }
    return [...deactivatedNodes];
  });
  const colorByValues = computed(() => {
    if (!animationConfig.value) return;
    if (animationConfig.value.time_mode === "slider") {
      return currentAnimationStateComponents.value?.map((component, index) => ({
        id: component.id,
        name: `Component ${index}`,
        node_ids: component.nodes,
      }));
    } else {
      let seen = new Set();
      return availableAnimationStates.value
        .toSorted((a, b) => a.index - b.index)
        .map((state) => {
          const deactivated = state.deactivated_nodes.filter(
            (nId) => !seen.has(nId),
          );
          seen = seen.union(new Set(deactivated));
          return {
            id: state.id,
            name: `State ${state.index}`,
            node_ids: deactivated,
          };
        })
        .filter((state) => state.node_ids.length > 0);
    }
  });
  const networkColors = computed(() => {
    if (
      !animationConfig.value?.colormap ||
      !currentAnimationStateComponents.value ||
      !colorByValues.value?.length
    ) {
      return {};
    }
    const colors = styleStore.colormapMarkersSubsample(
      animationConfig.value.colormap,
      {
        discrete: true,
        n_colors: colorByValues.value.length,
      },
    );
    if (!colors) return {};
    return Object.fromEntries(
      colorByValues.value.map((v, i) => [v.id, colors[i].color]),
    );
  });
  const nodeGroups = computed(() => {
    if (!colorByValues.value || !animationConfig.value) return;
    let groups = colorByValues.value.map((group) => ({
      id: group.id,
      name: group.name,
      node_ids: group.node_ids,
      color: networkColors.value[group.id],
      opacity: 1,
    }));
    if (animationConfig.value.time_mode === "slider") {
      groups = groups.map((group) => {
        group.node_ids = group.node_ids.filter(
          (nId) => !deactivatedNodes.value.includes(nId),
        );
        return group;
      });
      groups.push({
        id: -1,
        name: "Deactivated",
        node_ids: deactivatedNodes.value,
        color: animationConfig.value.deactivated_color,
        opacity: animationConfig.value.deactivated_opacity,
      });
    }
    return groups;
  });
  const nodeGroupsWithHighlights = computed(() => {
    if (!nodeGroups.value || !animationConfig.value) return;
    const groups = nodeGroups.value.map((group) => ({
      ...group,
      node_ids: group.node_ids.filter(
        (nId) => !hoverNodeIds.value.includes(nId),
      ),
    }));
    if (hoverNodeIds.value.length) {
      groups.push({
        id: -2,
        name: "Highlighted",
        node_ids: hoverNodeIds.value,
        color: animationConfig.value.hover_color,
        opacity: 1,
      });
    }
    return groups;
  });
  const nodeGroupColorMarkers = computed(() => {
    if (!currentNetwork.value || !nodeGroups.value) return [];
    const totalNodes = currentNetwork.value.counts.nodes;
    let visited = 0;
    const markers: { color: string; value: number }[] = nodeGroups.value
      .filter((group) => group.name !== "Highlighted")
      .map((group) => {
        visited += group.node_ids.length;
        return {
          color: group.color,
          node_ids: group.node_ids,
          value: visited / totalNodes,
          label: `${group.name} (${group.node_ids.length} nodes)`,
        };
      });
    if (animationConfig.value?.default_color) {
      markers.push({
        color: animationConfig.value?.default_color,
        value: 1,
      });
    }
    return markers;
  });

  function resetAnimationConfig() {
    animationConfig.value = {
      time_mode: "slider",
      interval: 1,
      colormap: styleStore.colormaps.find((c) => c.name === "viridis"),
      deactivated_color: "#ff0000",
      deactivated_opacity: 1,
      hover_color: "#feffab",
      default_color: appStore.theme === "dark" ? "#ffffff" : "#000000",
    };
  }

  async function initNetworks(projectId: number) {
    loadingNetworks.value = true;
    const networks = await getProjectNetworks(projectId);
    availableNetworks.value = networks;
    currentNetwork.value = undefined;
    loadingNetworks.value = false;
    resetAnimationConfig();
  }

  async function getNetwork(
    nodeId: number,
    dataset: Dataset,
  ): Promise<Network | undefined> {
    let network: Network | undefined;
    availableNetworks.value.forEach((net) => {
      if (net.nodes.includes(nodeId)) {
        network = net;
      }
    });
    if (!network) {
      availableNetworks.value = [
        ...availableNetworks.value,
        ...(await getDatasetNetworks(dataset.id)).filter((net) => {
          return !availableNetworks.value.map((n) => n.id).includes(net.id);
        }),
      ];
    }
    availableNetworks.value.forEach((net) => {
      if (net.nodes.includes(nodeId)) {
        network = net;
      }
    });
    if (availableNetworks.value.length) {
      panelStore.panelArrangement = panelStore.panelArrangement.map((panel) => {
        if (panel.id === "networks") panel.visible = true;
        return panel;
      });
    }
    currentNetwork.value = network;
    return network;
  }

  function currentAnimationUpdated() {
    currentAnimationTick.value = 0;
    if (animationConfig.value) {
      // Automatically switch to slider time mode
      animationConfig.value.time_mode = "slider";
    }
    if (!currentNetwork.value || !currentAnimation.value) {
      [networkNodesMapLayerId.value, networkEdgesMapLayerId.value].forEach(
        (mapLayerId) => {
          if (mapLayerId) {
            // Previously applied custom style to map layer, reset style with style store
            const layerInfo = mapStore.parseLayerString(mapLayerId);
            const layer = layerStore.selectedLayers.find(
              (l) => l.id === layerInfo.layerId,
            );
            if (layer) styleStore.updateLayerStyles(layer);
          }
        },
      );
      networkNodesMapLayerId.value = undefined;
      networkEdgesMapLayerId.value = undefined;
    } else {
      const networkMapLayers = mapStore
        .getUserMapLayers()
        .filter((mapLayerId) => {
          const layerInfo = mapStore.parseLayerString(mapLayerId);
          return (
            layerInfo.type === "vector" &&
            currentNetwork.value &&
            layerInfo.typeId === currentNetwork.value.vector_data
          );
        });
      networkNodesMapLayerId.value = networkMapLayers.find((mapLayerId) =>
        mapLayerId.includes("circle"),
      );
      networkEdgesMapLayerId.value = networkMapLayers.find((mapLayerId) =>
        mapLayerId.includes("line"),
      );
    }
  }

  async function fetchAnimations() {
    if (!currentNetwork.value || !projectStore.currentProject) {
      availableAnimations.value = [];
    } else {
      availableAnimations.value = await getNetworkAnimations(
        currentNetwork.value.id,
        projectStore.currentProject.id,
      );
    }
  }

  async function fetchAnimationStates() {
    if (!currentAnimation.value) {
      availableAnimationStates.value = [];
    } else {
      loadingStates.value = true;
      availableAnimationStates.value = await getNetworkAnimationStates(
        currentAnimation.value.id,
      );
      loadingStates.value = false;
    }
  }

  async function fetchAnimationSyncLayers() {
    if (!currentAnimation.value || !projectStore.currentProject) {
      currentAnimationSyncLayers.value = [];
      return;
    }
    currentAnimationSyncLayers.value = await Promise.all(
      currentAnimation.value.sync_layers.map(
        async (layerId) =>
          await getLayer(layerId, projectStore.currentProject?.id),
      ),
    );
  }

  async function createAnimation() {
    if (
      !projectStore.currentProject?.id ||
      !currentNetwork.value?.id ||
      !editAllowed.value ||
      !newAnimationName.value?.length
    ) {
      return;
    }
    currentAnimation.value = await createNetworkAnimation(
      newAnimationName.value,
      currentNetwork.value.id,
      projectStore.currentProject.id,
    );
    creatingAnimation.value = false;
    newAnimationName.value = undefined;
    await fetchAnimations();
  }

  async function deleteAnimation() {
    if (!animationToDelete.value) {
      return;
    }
    if (currentAnimation.value?.id === animationToDelete.value.id) {
      currentAnimation.value = undefined;
    }
    await deleteNetworkAnimation(animationToDelete.value.id);
    await fetchAnimations();
    animationToDelete.value = undefined;
  }

  async function createAnimationState() {
    if (!currentAnimation.value) {
      return;
    }
    loadingStates.value = true;
    const index = availableAnimationStates.value.length;
    await createNetworkAnimationState(
      currentAnimation.value.id,
      index,
      deactivatedNodes.value,
    );
    await fetchAnimationStates();
    currentAnimationTick.value = index;
  }

  async function deleteAnimationState() {
    if (!stateToDelete.value || !editAllowed.value || !currentAnimation.value) {
      return;
    }
    loadingStates.value = true;
    await deleteNetworkAnimationState(stateToDelete.value.id);
    await fetchAnimationStates();
    if (currentAnimationTick.value == stateToDelete.value.index) {
      currentAnimationTick.value = availableAnimationStates.value.length - 1;
    }
    stateToDelete.value = undefined;
  }

  function updateNetworkLayerStyle() {
    if (
      !currentNetwork.value ||
      !networkNodesMapLayerId.value ||
      !networkEdgesMapLayerId.value ||
      !currentAnimationState.value ||
      !animationConfig.value ||
      !nodeGroupsWithHighlights.value
    ) {
      return;
    }
    const map = mapStore.getMap();
    if (
      !map.getLayer(networkNodesMapLayerId.value) ||
      !map.getLayer(networkEdgesMapLayerId.value)
    ) {
      return;
    }

    let nodeColor: any, edgeColor: any, nodeOpacity: any, edgeOpacity: any;
    if (!nodeGroupsWithHighlights.value.length) {
      nodeColor = animationConfig.value.default_color;
      edgeColor = animationConfig.value.default_color;
      nodeOpacity = 1;
      edgeOpacity = 1;
    } else {
      nodeColor = ["case"];
      edgeColor = ["case"];
      nodeOpacity = ["case"];
      edgeOpacity = ["case"];

      nodeGroupsWithHighlights.value.forEach((group) => {
        // Group conditions
        const nodeCondition = [
          "in",
          ["get", "node_id"],
          ["literal", group.node_ids],
        ];
        const edgeCondition = [
          // If group represents deactivated or hover nodes, include edge if either to or from node is included
          // Otherwise only include edge if both to and from are included
          ["Deactivated", "Highlighted"].includes(group.name) ? "any" : "all",
          ["in", ["get", "from_node_id"], ["literal", group.node_ids]],
          ["in", ["get", "to_node_id"], ["literal", group.node_ids]],
        ];
        nodeColor.push(nodeCondition);
        nodeOpacity.push(nodeCondition);
        edgeColor.push(edgeCondition);
        edgeOpacity.push(edgeCondition);
        // Group outputs
        nodeColor.push(group.color);
        nodeOpacity.push(group.opacity);
        edgeColor.push(group.color);
        edgeOpacity.push(group.opacity);
      });
      // Fallback outputs
      nodeColor.push(animationConfig.value.default_color);
      edgeColor.push(animationConfig.value.default_color);
      nodeOpacity.push(1);
      edgeOpacity.push(1);
    }

    map.setPaintProperty(
      networkNodesMapLayerId.value,
      "circle-color",
      nodeColor,
    );
    map.setPaintProperty(
      networkNodesMapLayerId.value,
      "circle-stroke-color",
      nodeColor,
    );
    map.setPaintProperty(networkEdgesMapLayerId.value, "line-color", edgeColor);
    map.setPaintProperty(
      networkNodesMapLayerId.value,
      "circle-opacity",
      nodeOpacity,
    );
    map.setPaintProperty(
      networkNodesMapLayerId.value,
      "circle-stroke-opacity",
      nodeOpacity,
    );
    map.setPaintProperty(
      networkEdgesMapLayerId.value,
      "line-opacity",
      edgeOpacity,
    );
  }

  const debouncedUpdateNetworkLayerStyle = debounce(
    updateNetworkLayerStyle,
    100,
  );

  function updateSyncLayers() {
    if (!currentAnimationSyncLayers.value?.length) return;
    currentAnimationSyncLayers.value.forEach((syncLayer) => {
      if (panelStore.isVisible({ layer: syncLayer })) {
        const selectedLayer = layerStore.selectedLayers.find(
          (l) => l.id === syncLayer.id,
        );
        if (selectedLayer) {
          selectedLayer.current_frame_index = currentAnimationTick.value;
          layerStore.updateLayerFrame(selectedLayer);
        }
      }
    });
  }

  function toggleNodeActive(nodeId: number) {
    if (!currentAnimationState.value) return;
    availableAnimationStates.value = availableAnimationStates.value.map(
      (state) => {
        if (state.index >= currentAnimationTick.value) {
          if (state.deactivated_nodes.includes(nodeId)) {
            state.deactivated_nodes = state.deactivated_nodes.filter(
              (nId) => nId !== nodeId,
            );
          } else {
            state.deactivated_nodes.push(nodeId);
          }
        }
        return state;
      },
    );
    animationNeedsSave.value = true;
    updateNetworkLayerStyle();
  }

  async function saveAnimationStates() {
    animationSaveLoading.value = true;
    await Promise.all(
      availableAnimationStates.value.map(updateNetworkAnimationState),
    );
    await fetchAnimationStates();
    animationSaveLoading.value = false;
    animationNeedsSave.value = false;
    updateNetworkLayerStyle();
  }

  watch(currentNetwork, () => {
    availableAnimations.value = [];
    currentAnimation.value = undefined;
    currentAnimationTick.value = 0;
    availableAnimationStates.value = [];
    resetAnimationConfig();
    fetchAnimations();
  });
  watch(currentAnimation, async () => {
    currentAnimationUpdated();
    await fetchAnimationStates();
    await fetchAnimationSyncLayers();
    updateNetworkLayerStyle();
  });
  watch(currentAnimationTick, () => {
    debouncedUpdateNetworkLayerStyle();
    updateSyncLayers();
  });
  watch(animationConfig, debouncedUpdateNetworkLayerStyle, { deep: true });
  watch(hoverNodeIds, debouncedUpdateNetworkLayerStyle);
  watch(
    () => appStore.theme,
    () => {
      if (
        appStore.theme === "dark" &&
        animationConfig.value?.default_color === "#000000"
      ) {
        animationConfig.value.default_color = "#ffffff";
      } else if (
        appStore.theme === "light" &&
        animationConfig.value?.default_color === "#ffffff"
      ) {
        animationConfig.value.default_color = "#000000";
      }
    },
  );

  return {
    availableNetworks,
    currentNetwork,
    availableAnimations,
    currentAnimation,
    currentAnimationTick,
    currentAnimationSyncLayers,
    animationConfig,
    availableAnimationStates,
    currentAnimationState,
    currentAnimationStateComponents,
    loadingNetworks,
    loadingStates,
    initNetworks,
    getNetwork,
    fetchAnimations,
    fetchAnimationStates,
    deactivatedNodes,
    networkColors,
    nodeGroupColorMarkers,
    hoverNodeIds,
    networkNodesMapLayerId,
    networkEdgesMapLayerId,
    updateNetworkLayerStyle,
    updateSyncLayers,
    editAllowed,
    currentAnimationEditable,
    creatingAnimation,
    newAnimationName,
    createAnimation,
    deleteAnimation,
    animationToDelete,
    createAnimationState,
    deleteAnimationState,
    stateToDelete,
    toggleNodeActive,
    animationNeedsSave,
    animationSaveLoading,
    saveAnimationStates,
  };
});
